import moment from 'moment'
import { EmptyFilePropsFactory, IFileProps } from './File'
import {
  UserNames,
  UserProperties,
  UserFlags,
  UserJob,
  UserSupervisor,
  UserPayments,
  UserApplication,
  UserMaintenance,
  UserCertificate,
  UserPharmaceutical,
  UserDepartment,
  UserName,
  UserHospital,
  UserIndustry
} from '@/infrastructure/network/Monitaro/schema/models'
import RelationCompany from '@/infrastructure/network/Monitaro/schema/models/RelationCompany'

export interface IUserProps {
  id?: string
  officeId?: string | null
  companyId?: string | null
  company?: RelationCompany | null
  industryId?: string | null
  industry?: UserIndustry | null
  hospitals: UserHospital[]
  pharmaceuticals?: UserPharmaceutical[] | null
  pharmaceutical?: string[] | null
  departments?: UserDepartment[] | null
  username: string | null
  login: string | null
  fcmToken: string | null
  uuid: string | null
  fullname: string | null
  kana: string | null
  organization: string | null
  name: UserNames | null
  properties: UserProperties
  flags: UserFlags | null
  job: UserJob | null
  supervisor: UserSupervisor | null
  payments: UserPayments | null
  application: UserApplication | null
  maintenance: UserMaintenance | null
  certificate: UserCertificate | null
  provider: any | null
  status: string | null
  activation: boolean | null
  withdrawedAt: string | null
  lockedAt: string | null
  canceledAt: string | null
  stoppedAt: string | null
  createdAt: string | null
  updatedAt: string | null
  files: IUserFilesProps
  data: IUserDataProps
  favorite: boolean | null
}
export interface IUserQueryProps {
  status: string[] | null
  industries: string[] | null
}

export interface IUserFilesProps {
  image: IFileProps | null
  card: IFileProps | null
}

export interface IUserDataProps {
  image: File | null
  card: File | null
}

export default class UserEntity {
  private _props: IUserProps

  constructor(props?: IUserProps | null) {
    this._props = EmptyUserPropsFactory(props)
  }

  get props(): IUserProps {
    return this._props
  }

  get clone(): IUserProps {
    return structuredClone(this._props)
  }

  get id(): string | undefined {
    return this._props.id
  }

  get companyId(): string | null | undefined {
    return this._props.companyId
  }

  get industryId(): string | null | undefined {
    return this._props.industryId
  }

  get hospitalId(): string | null | undefined {
    return this._props.hospitals ? this._props.hospitals[0]?.id || null : null
  }

  get username(): string | null {
    return this._props.username
  }

  get login(): string | null {
    return this._props.login
  }

  get fcmToken(): string | null {
    return this._props.fcmToken
  }

  get uuid(): string | null {
    return this._props.uuid
  }

  get fullname(): string | null {
    return this._props.fullname
  }

  get kana(): string | null {
    return this._props.kana
  }

  get organization(): string | null {
    return this._props.organization
  }

  get name(): UserNames | null | undefined {
    return this._props.name
  }

  get icon(): string | null {
    return this._props.files.image?.path || null
  }

  get properties(): UserProperties | null | undefined {
    return this._props.properties
  }

  get flags(): UserFlags | null | undefined {
    return this._props.flags
  }

  get job(): UserJob | null | undefined {
    return this._props.job
  }

  get supervisor(): UserSupervisor | null | undefined {
    return this._props.supervisor
  }

  get payments(): UserPayments | null | undefined {
    return this._props.payments
  }

  get application(): UserApplication | null | undefined {
    return this._props.application
  }

  get maintenance(): UserMaintenance | null | undefined {
    return this._props.maintenance
  }

  get certificate(): UserCertificate | null | undefined {
    return this._props.certificate
  }

  get files(): IUserFilesProps {
    return this._props.files
  }

  get status(): string | null {
    return this._props.status
  }

  get activation(): boolean | null {
    return this._props.activation
  }

  get withdrawedAt(): string | null {
    return this._props.withdrawedAt
  }

  get lockedAt(): string | null {
    return this._props.lockedAt
  }

  get canceledAt(): string | null {
    return this._props.canceledAt
  }

  get stoppedAt(): string | null {
    return this._props.stoppedAt
  }

  get createdAt(): string | null {
    return this._props.createdAt ? moment(this._props.createdAt).format('YYYY/MM/DD') : null
  }

  get updatedAt(): string | null {
    return this._props.updatedAt
  }

  get initialize(): string {
    return this._props.properties?.initialize || 'profile'
  }

  get chargeable(): boolean | null {
    return this._props.industry?.chargeable === 'normal'
  }

  get favorite(): boolean {
    return !!this._props.favorite
  }

  get method(): string | null {
    return this._props.payments?.type || null
  }

  get role(): string {
    let role = 'normal'

    // 非課金ユーザー
    if (!this.chargeable) {
      role = 'unchargeable'
    }

    // ロックユーザー
    if (this._props.flags?.locked) {
      role = 'locked'
    }

    return role
  }

  get invoice(): boolean {
    if (this._props.company?.properties?.invoice) {
      return !!this._props.officeId
    }
    return false
  }
}

export const headers = [{ text: 'ID', value: 'id' }]

export const accounts = [
  { text: '基本情報', value: 'profile' },
  { text: '勤務先情報', value: 'profile-company' },
  { text: '担当情報', value: 'profile-manage' },
  { text: '上司', value: 'profile-supervisor' }
]

export const EmptyUserPropsFactory = (props?: Partial<IUserProps> | null): IUserProps => ({
  hospitals: [],
  pharmaceuticals: null,
  pharmaceutical: [''],
  username: null,
  login: null,
  fcmToken: null,
  uuid: null,
  fullname: null,
  kana: null,
  organization: null,
  name: null,
  payments: null,
  application: null,
  maintenance: null,
  certificate: null,
  provider: null,
  status: null,
  activation: null,
  withdrawedAt: null,
  lockedAt: null,
  canceledAt: null,
  stoppedAt: null,
  createdAt: null,
  updatedAt: null,
  favorite: false,
  ...props,
  properties: EmptyUserPropertiesFactory(props?.properties),
  supervisor: EmptyUserSupervisorFactory(props?.supervisor),
  job: EmptyUserJobFactory(props?.job),
  flags: EmptyUserFlagsFactory(props?.flags),
  files: EmptyUserFilesPropsFactory(props?.files),
  data: EmptyUserDataPropsFactory(props?.data)
})

export const EmptyUserNamesFactory = (props?: Partial<UserNames> | null): UserNames => ({
  primary: EmptyUserNameFactory(props?.primary),
  kana: EmptyUserNameFactory(props?.kana),
  ...props
})

export const EmptyUserNameFactory = (props?: Partial<UserName> | null): UserName => ({
  family: null,
  given: null,
  ...props
})

export const EmptyUserPropertiesFactory = (props?: Partial<UserProperties> | null): UserProperties => ({
  identifier: null,
  activation: null,
  phone: null,
  gender: null,
  birthday: null,
  experience: null,
  academic: null,
  mr: null,
  initialize: null,
  filter: null,
  ...props
})

export const EmptyUserSupervisorFactory = (props?: Partial<UserSupervisor> | null): UserSupervisor => ({
  phone: null,
  email: null,
  notification: null,
  ...props,
  name: EmptyUserNamesFactory(props?.name)
})

export const EmptyUserJobFactory = (props?: Partial<UserJob> | null): UserJob => ({
  zipcode: null,
  prefecture: null,
  city: null,
  building: null,
  phone: null,
  customer: null,
  division: null,
  branch: null,
  position: null,
  ...props
})

export const EmptyUserFlagsFactory = (props?: Partial<UserFlags> | null): UserFlags => ({
  locked: false,
  identification: false,
  chargeable: false,
  chat: false,
  valid_email: false,
  withdraw: false,
  ...props
})

export const EmptyUserDataPropsFactory = (props?: Partial<IUserDataProps> | null): IUserDataProps => ({
  image: null,
  card: null,
  ...props
})

export const EmptyUserFilesPropsFactory = (props?: Partial<IUserFilesProps> | null): IUserFilesProps => ({
  image: EmptyFilePropsFactory(),
  card: EmptyFilePropsFactory(),
  ...props
})
